import { Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { ROUTES } from "@/config/routes";
import { site } from "@/config/site";
import { BrandLogo } from "@/features/layout/components/BrandLogo";
import { ExternalLinkButton } from "@/pages/auth/ExternalLinkButton";

const STEPS = [
  {
    title: "Open Stripe Dashboard",
    body: "Log in to the account where InvoiceVault is installed.",
  },
  {
    title: "Click any InvoiceVault link",
    body: "Use the drawer — e.g. Download Invoice PDFs or Export History.",
  },
] as const;

export function SessionExpiredPage() {
  return (
    <div className="flex min-h-screen flex-col bg-[var(--bg)]">
      <header className="border-b border-[var(--border-soft)] bg-[var(--surface)] px-6 py-4">
        <BrandLogo />
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-10">
        <div className="w-full max-w-[420px] text-center">
          <div
            className="mx-auto mb-5 grid h-16 w-16 place-items-center rounded-full border-2 border-amber-200 bg-amber-100 text-amber-600"
            aria-hidden
          >
            <Clock className="h-8 w-8" strokeWidth={1.75} />
          </div>

          <h1 className="text-2xl font-bold tracking-tight text-[var(--text)]">Session expired</h1>
          <p className="mx-auto mt-2 max-w-sm text-[0.9375rem] leading-relaxed text-[var(--text-secondary)]">
            Your sign-in has ended. Re-open InvoiceVault from Stripe Dashboard — there is no password on
            this site.
          </p>

          <div className="mt-6 rounded-[var(--radius)] border border-[var(--border)] bg-white px-5 py-5 text-left shadow-sm">
            <p className="mb-3.5 text-xs font-bold uppercase tracking-wider text-[var(--muted)]">What to do</p>
            <ol className="space-y-0">
              {STEPS.map((step, index) => (
                <li
                  key={step.title}
                  className={index > 0 ? "mt-3.5 border-t border-[var(--border-soft)] pt-3.5" : undefined}
                >
                  <div className="flex gap-3">
                    <span className="grid h-[1.375rem] w-[1.375rem] shrink-0 place-items-center rounded-full bg-amber-50 text-[0.6875rem] font-bold text-amber-800">
                      {index + 1}
                    </span>
                    <div>
                      <strong className="block text-sm font-semibold text-[var(--text)]">{step.title}</strong>
                      <span className="mt-0.5 block text-[0.8125rem] leading-snug text-[var(--text-secondary)]">
                        {step.body}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="mt-5">
            <ExternalLinkButton href={site.stripeDashboardUrl} className="py-3 text-[0.9375rem]">
              Open Stripe Dashboard
            </ExternalLinkButton>
          </div>
          <p className="mt-3 text-[0.8125rem] leading-relaxed text-[var(--muted)]">
            Sessions last {site.sessionTtlHours} hours. Signing in again takes a few seconds.
          </p>

          <p className="mt-5 text-sm">
            <Link to={ROUTES.help} className="font-semibold text-[var(--accent)]">
              Help &amp; Support
            </Link>
          </p>
        </div>
      </main>

      <footer className="border-t border-[var(--border-soft)] bg-[var(--surface)] px-6 py-4 text-center text-[0.8125rem] text-[var(--muted)]">
        © {site.brandName} · {site.name}
      </footer>
    </div>
  );
}
